"use client";

import { ModalStackDepthReporter } from "@klarify/ui/hooks/use-modal-registry";
import { cn } from "@klarify/ui/lib/utils";
import { Trans } from "@lingui/react/macro";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";
import type * as React from "react";
import { useState } from "react";

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) => (
	<SheetPrimitive.Overlay
		className={cn(
			"data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80 data-[state=closed]:animate-out data-[state=open]:animate-in",
			className,
		)}
		{...props}
		ref={ref}
	/>
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva(
	"fixed z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:duration-300 data-[state=open]:duration-500",
	{
		variants: {
			side: {
				top: "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b",
				bottom:
					"data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t",
				left: "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
				right:
					"data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
			},
		},
		defaultVariants: {
			side: "right",
		},
	},
);

interface SheetContentProps
	extends React.ComponentProps<typeof SheetPrimitive.Content>,
		VariantProps<typeof sheetVariants> {
	closeIcon?: React.ReactNode;
	closeIconPosition?: {
		top?: string | number;
		right?: string | number;
		bottom?: string | number;
		left?: string | number;
	};
	hideCloseButtonFocusRing?: boolean;
	hideCloseButton?: boolean;
}

const SheetContent = ({
	ref,
	side = "right",
	hideCloseButtonFocusRing = false,
	hideCloseButton = false,
	className,
	style,
	children,
	closeIcon,
	closeIconPosition,
	...props
}: SheetContentProps) => {
	// Depth comes from a reporter inside the Presence-gated content: it only
	// registers while the sheet is open, so closed-but-mounted sheets never
	// count toward the open-modal registry (which would suppress the
	// product-update CTAs) and never skew stacked z-index offsets.
	const [depth, setDepth] = useState(0);
	const stackOffset = Math.max(depth - 1, 0) * 20;
	// Skip inline z-index for unstacked sheets so child portals (Popover,
	// Select, DropdownMenu — all z-50) keep the same stacking baseline as
	// before and continue to win via DOM order.
	const overlayStyle =
		stackOffset > 0 ? { zIndex: 50 + stackOffset } : undefined;
	const contentStyle =
		stackOffset > 0 ? { ...style, zIndex: 50 + stackOffset } : style;
	const defaultPosition = {
		top: "1rem",
		right: "1rem",
	};
	const position = { ...defaultPosition, ...closeIconPosition };
	return (
		<SheetPortal>
			<SheetOverlay style={overlayStyle} />
			<SheetPrimitive.Content
				ref={ref}
				className={cn(sheetVariants({ side }), className)}
				style={contentStyle}
				{...props}
			>
				<ModalStackDepthReporter onDepthChange={setDepth} />
				{children}
				{!hideCloseButton && (
					<SheetPrimitive.Close
						className={cn(
							"rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-secondary",
							!hideCloseButtonFocusRing &&
								"focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2",
							"z-51",
						)}
						style={{
							position: "absolute",
							top: position.top,
							right: position.right,
							bottom: position.bottom,
							left: position.left,
						}}
					>
						{closeIcon ?? <Cross2Icon className="h-4 w-4" />}
						<span className="sr-only">
							<Trans>Close</Trans>
						</span>
					</SheetPrimitive.Close>
				)}
			</SheetPrimitive.Content>
		</SheetPortal>
	);
};
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.ComponentProps<"div">) => (
	<div
		className={cn(
			"flex flex-col space-y-2 text-center sm:text-left",
			className,
		)}
		{...props}
	/>
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.ComponentProps<"div">) => (
	<div
		className={cn(
			"flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
			className,
		)}
		{...props}
	/>
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) => (
	<SheetPrimitive.Title
		ref={ref}
		className={cn("font-semibold text-foreground text-lg", className)}
		{...props}
	/>
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = ({
	ref,
	className,
	...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn("text-muted-foreground text-sm", className)}
		{...props}
	/>
);
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetFooter,
	SheetHeader,
	SheetOverlay,
	SheetPortal,
	SheetTitle,
	SheetTrigger,
};
